// When each sound plays (audio §3), driven by the client event bus (events/bus.ts).
//
// - Pure rules: `classifyEat` (eat record → slots, audio §3 rows 3–6), `canSplitSound` /
//   `canShootSound` (rows 1–2 gating on the own-cell snapshot taken when the intent was sent).
// - `attachAudioTriggers(bus, player)` subscribes the engine to the bus:
//   - `eat` (render time, in sync with the drawing) → `death | eat`, then `merge`, then `pop`;
//     in a money room an own cell eating another player's cell layers `moneyGain` just after;
//   - `ownDeath` → `death` only if no last-own-cell eat played it within DEATH_DEDUP_MS before,
//     and none arrives within DEATH_DEDUP_MS after (the render lags the DEATH packet);
//   - `splitSent` / `ejectSent` (key press, touch button, every hold-W repeat) → split / shoot;
//   - `uiClick` → click;
//   - money mode (ball.io extension): `ownCashOutStart` → cashStart, `ownCashOutProgress` →
//     cashTick (pitch rises with the hold), `ownCashOutStop` (cancelled) → cashCancel after
//     CASH_CANCEL_DEFER_MS unless the life ends meanwhile,
//     `cashOutDone` → cashOut, `cashOutTooEarly` / `moneyError` → error, `payoutDelayed` → notice.
//   No sound on spawn, respawn, menu open, toast, leaderboard change or party join (audio §3).
//   The engine applies the settings gates, caps and guards.
// - It marks those slots bus-driven on the engine, so app.ts's legacy direct `play` calls for
//   split / shoot / death / click are ignored instead of doubling the sound.
//
// Implementer: client-audio (lane A). Consumer: app.ts (`attachAudioTriggers(this.bus, this.sfx)`
// once in start(), disposer on stop).

import { KIND, SIM_DEFAULTS } from '@ballio/shared';
import type { ClientBus, OwnSnapshot } from '../events/bus.js';
import type { EatEvent } from '../game/world-store.js';
import type { SoundPlayer } from './engine.js';
import type { SoundSlot } from './recipes.js';

/** Split sound needs an own cell above this mass (r > 60; audio §3 row 1). */
export const SPLIT_SOUND_MIN_MASS = 36;
/** Shoot sound needs an own cell above this mass (audio §3 row 2). */
export const SHOOT_SOUND_MIN_MASS = 36.125;
/** DEATH ↔ last-own-cell eat de-duplication window (audio §3 row 6). */
export const DEATH_DEDUP_MS = 300;
/** moneyGain starts this long after the gulp of the eat it rewards. */
export const MONEY_GAIN_DELAY_MS = 45;
/**
 * A cancelled hold's cashCancel waits this long (ms) and is dropped if the life ends meanwhile
 * (a hold cut short by death plays only `death`).
 */
export const CASH_CANCEL_DEFER_MS = 80;
/** cashTick playback rate at full progress (a fifth above the start). */
export const CASH_TICK_MAX_RATE = 2 ** (7 / 12);

/** The slots `attachAudioTriggers` drives from the bus. */
export const BUS_DRIVEN_SLOTS: readonly SoundSlot[] = Object.freeze([
  'split',
  'shoot',
  'eat',
  'merge',
  'pop',
  'death',
  'click',
  'cashStart',
  'cashTick',
  'cashOut',
  'cashCancel',
  'moneyGain',
  'notice',
  'error',
] satisfies SoundSlot[]);

const NONE: readonly SoundSlot[] = Object.freeze([]);
const EAT: readonly SoundSlot[] = Object.freeze(['eat'] satisfies SoundSlot[]);
const EAT_MERGE: readonly SoundSlot[] = Object.freeze(['eat', 'merge'] satisfies SoundSlot[]);
const DEATH: readonly SoundSlot[] = Object.freeze(['death'] satisfies SoundSlot[]);
const POP: readonly SoundSlot[] = Object.freeze(['pop'] satisfies SoundSlot[]);

/** The eat-record fields the rules read. */
export type EatRecord = Pick<EatEvent, 'eatenKind' | 'eatenOwn' | 'eaterOwn' | 'ownLeft'>;

/**
 * The slots one eat record plays, in play order (audio §3 rows 3–6; frozen shared arrays, no
 * allocation):
 * - a player cell eaten: `death` if it was the last own cell (ownLeft 0), else `eat` — also when
 *   neither cell is own and while dead or spectating; both cells own → `merge` in addition;
 * - a virus eaten → `pop`, no ownership check;
 * - pellets, ejected mass, spawners → nothing.
 */
export function classifyEat(ev: EatRecord): readonly SoundSlot[] {
  if (ev.eatenKind === KIND.CELL) {
    if (ev.eatenOwn && ev.eaterOwn) return EAT_MERGE;
    if (ev.eatenOwn && ev.ownLeft === 0) return DEATH;
    return EAT;
  }
  if (ev.eatenKind === KIND.VIRUS) return POP;
  return NONE;
}

/** Split sound gate: 1–15 own cells and at least one above SPLIT_SOUND_MIN_MASS. */
export function canSplitSound(own: OwnSnapshot): boolean {
  return (
    own.ownCells >= 1 &&
    own.ownCells < SIM_DEFAULTS.cells.max &&
    own.maxOwnMass > SPLIT_SOUND_MIN_MASS
  );
}

/** Shoot sound gate: at least one own cell above SHOOT_SOUND_MIN_MASS. */
export function canShootSound(own: OwnSnapshot): boolean {
  return own.ownCells >= 1 && own.maxOwnMass > SHOOT_SOUND_MIN_MASS;
}

/** cashTick playback rate for a 0–1 hold progress (deterministic: same progress, same pitch). */
export function cashTickRate(progress: number): number {
  const p = Number.isFinite(progress) ? Math.min(1, Math.max(0, progress)) : 0;
  return CASH_TICK_MAX_RATE ** p;
}

export interface AudioTriggerOptions {
  /** ms clock (default `performance.now`). */
  readonly now?: () => number;
  readonly setTimeout?: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout?: (handle: unknown) => void;
}

/** Subscribes `player` to `bus` (see the header); returns the disposer. */
export function attachAudioTriggers(
  bus: ClientBus,
  player: SoundPlayer,
  opts: AudioTriggerOptions = {},
): () => void {
  const now = opts.now ?? (() => performance.now());
  const setT =
    opts.setTimeout ?? ((fn: () => void, ms: number): unknown => globalThis.setTimeout(fn, ms));
  const clearT =
    opts.clearTimeout ??
    ((h: unknown): void => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>));

  let moneyRoom = false;
  /** Territory arena room: its death sound is the arena client's (`deathArena`, audio row 3). */
  let arenaRoom = false;
  let lastDeathMs = Number.NEGATIVE_INFINITY;
  let pendingDeath: unknown = null;
  let pendingCancel: unknown = null;
  let lastLifeEndMs = Number.NEGATIVE_INFINITY;
  const rateOpts: { rate: number } = { rate: 1 };
  const gainOpts = Object.freeze({ delayMs: MONEY_GAIN_DELAY_MS });

  const cancelPending = (): void => {
    if (pendingDeath === null) return;
    clearT(pendingDeath);
    pendingDeath = null;
  };
  const dropCancel = (): void => {
    if (pendingCancel === null) return;
    clearT(pendingCancel);
    pendingCancel = null;
  };
  /** The life ended (death or cash-out): a deferred cashCancel no longer applies. */
  const lifeEnded = (t: number): void => {
    lastLifeEndMs = t;
    dropCancel();
  };

  player.setBusDriven(BUS_DRIVEN_SLOTS);

  const offs = [
    bus.on('welcome', ({ msg, money }) => {
      moneyRoom = money !== null;
      arenaRoom = msg.arena !== undefined;
      cancelPending();
      dropCancel();
    }),
    bus.on('eat', (ev) => {
      const t = now();
      const slots = classifyEat(ev);
      for (let i = 0; i < slots.length; i++) {
        const slot = slots[i] as SoundSlot;
        if (slot === 'death') {
          lastDeathMs = t;
          cancelPending();
          lifeEnded(t);
        }
        player.trigger(slot, t);
      }
      if (moneyRoom && ev.eatenKind === KIND.CELL && ev.eaterOwn && !ev.eatenOwn) {
        player.trigger('moneyGain', t, gainOpts);
      }
    }),
    bus.on('ownDeath', () => {
      const t = now();
      lifeEnded(t);
      if (arenaRoom) return;
      if (t - lastDeathMs < DEATH_DEDUP_MS) return;
      cancelPending();
      pendingDeath = setT(() => {
        pendingDeath = null;
        const t2 = now();
        if (t2 - lastDeathMs < DEATH_DEDUP_MS) return;
        lastDeathMs = t2;
        player.trigger('death', t2);
      }, DEATH_DEDUP_MS);
    }),
    bus.on('splitSent', (own) => {
      if (canSplitSound(own)) player.trigger('split', now());
    }),
    bus.on('ejectSent', (own) => {
      if (canShootSound(own)) player.trigger('shoot', now());
    }),
    bus.on('uiClick', () => {
      player.trigger('click', now());
    }),
    bus.on('ownCashOutStart', () => {
      player.trigger('cashStart', now());
    }),
    bus.on('ownCashOutProgress', ({ progress }) => {
      rateOpts.rate = cashTickRate(progress);
      player.trigger('cashTick', now(), rateOpts);
    }),
    bus.on('ownCashOutStop', ({ completed }) => {
      if (completed) return;
      const t = now();
      if (t - lastLifeEndMs < DEATH_DEDUP_MS) return;
      dropCancel();
      pendingCancel = setT(() => {
        pendingCancel = null;
        player.trigger('cashCancel', now());
      }, CASH_CANCEL_DEFER_MS);
    }),
    bus.on('cashOutDone', () => {
      const t = now();
      lifeEnded(t);
      player.trigger('cashOut', t);
    }),
    bus.on('cashOutTooEarly', () => {
      player.trigger('error', now());
    }),
    bus.on('moneyError', () => {
      player.trigger('error', now());
    }),
    bus.on('payoutDelayed', () => {
      player.trigger('notice', now());
    }),
  ];

  return () => {
    for (const off of offs) off();
    cancelPending();
    dropCancel();
    player.setBusDriven([]);
  };
}
